import { useEffect, useRef, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { formatPhoneNumber } from "@src/common/lib/textUtils";

const SEARCH_CALLER_CONTACTS = gql`
  query SearchCallerContacts(
    $courierId: ID!
    $endCustomerId: ID
    $nameSearch: String
  ) {
    searchCallerContacts(
      courierId: $courierId
      endCustomerId: $endCustomerId
      nameSearch: $nameSearch
    ) {
      id
      endCustomerId
      name
      phone
      emails
      endCustomer {
        id
        firstName
        lastName
        companyName
      }
    }
  }
`;

export interface CallerContactOption {
  id: string;
  endCustomerId: string;
  name: string;
  phone: string | null;
  emails: string[] | null;
  endCustomerName: string;
  isSelectedCustomer: boolean;
}

interface CallerContactResult {
  id: string;
  endCustomerId: string;
  name: string;
  phone: string | null;
  emails: string[] | null;
  endCustomer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
  };
}

export function useCallerContactSearch(
  selectedCustomerId: string | undefined
) {
  const { courierId } = useAuth();
  const showError = useErrorToast();
  const [options, setOptions] = useState<CallerContactOption[]>([]);
  // Bumped whenever the customer changes so an in-flight search for the old
  // customer can't fill the list after the switch (CRI-185).
  const searchGeneration = useRef(0);

  useEffect(() => {
    searchGeneration.current += 1;
    setOptions([]);
  }, [selectedCustomerId]);

  const [searchCallerContacts, { loading }] = useLazyQuery(
    SEARCH_CALLER_CONTACTS,
    {
      fetchPolicy: "no-cache",
      onError: (error) => showError(error),
    }
  );

  const search = (query: string) => {
    if (!query || !courierId) {
      setOptions([]);
      return;
    }

    const generation = searchGeneration.current;
    searchCallerContacts({
      variables: {
        courierId,
        endCustomerId: selectedCustomerId,
        nameSearch: query,
      },
    }).then((res) => {
      if (generation !== searchGeneration.current) return;
      const contacts: CallerContactResult[] =
        res.data?.searchCallerContacts || [];

      const mapped: CallerContactOption[] = contacts.map((contact) => ({
        id: contact.id,
        endCustomerId: contact.endCustomerId,
        name: contact.name,
        phone: contact.phone ? formatPhoneNumber(contact.phone) : null,
        emails: contact.emails,
        endCustomerName:
          `${contact.endCustomer.firstName || ""} ${contact.endCustomer.lastName || ""}`.trim() ||
          contact.endCustomer.companyName ||
          "",
        isSelectedCustomer: contact.endCustomerId === selectedCustomerId,
      }));

      mapped.sort((a, b) => {
        if (a.isSelectedCustomer && !b.isSelectedCustomer) return -1;
        if (!a.isSelectedCustomer && b.isSelectedCustomer) return 1;
        return a.name.localeCompare(b.name);
      });

      setOptions(mapped);
    });
  };

  return { search, options, loading };
}
